<template>
  <studio-tool-shell
    title="Öffnungszeiten"
    description="Check-in-Freigaben und publizierte Öffnungszeiten pro Studio übersichtlich pflegen."
    icon="mdi-clock-edit-outline">
    <section class="hours-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Standortzeiten</div>
          <h2>Öffnungszeiten nach Studio</h2>
          <p>Abschnitte und Zeitfenster können unabhängig pro Standort verwaltet werden.</p>
        </div>
        <span class="count-pill">{{ openingHoursStudios.length }} Studios</span>
      </div>

      <v-expansion-panels v-model="panel" multiple dark flat class="hours-panels">
        <v-expansion-panel v-for="studio in openingHoursStudios" :key="studio._id" class="hours-panel">
          <v-expansion-panel-header class="hours-panel-header">
            <div class="studio-summary">
              <span class="studio-mark"><v-icon>mdi-office-building-marker-outline</v-icon></span>
              <div>
                <strong>{{ studio.name }}</strong
                ><span>{{ studio.openingHours.length }} Abschnitte</span>
              </div>
              <span class="status-pill" :class="{ active: studio.alwaysCheckin }">{{ studio.alwaysCheckin ? "24H Check-in" : "Zeitgesteuert" }}</span>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content class="hours-panel-content">
            <div class="checkin-card">
              <div><strong>24H Check-in</strong><span>Mitglieder dürfen unabhängig von den betreuten Zeiten einchecken.</span></div>
              <v-switch v-model="studio.alwaysCheckin" color="#009640" inset hide-details></v-switch>
            </div>

            <section v-for="(openingSection, sectionIndex) in studio.openingHours" :key="`opening-section-${sectionIndex}`" class="schedule-section">
              <div class="section-header">
                <v-text-field
                  v-model="openingSection.title"
                  label="Übertitel"
                  placeholder="z.B. Betreute Öffnungszeiten"
                  outlined
                  dense
                  dark
                  hide-details="auto"></v-text-field>
                <v-btn
                  icon
                  class="icon-action danger"
                  aria-label="Abschnitt löschen"
                  @click="removeOpeningHourTable(sectionIndex, studio.openingHours)">
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </div>

              <div v-if="!openingSection.hours.length" class="empty-row">
                <v-icon>mdi-clock-alert-outline</v-icon><span>Noch keine Zeitangabe in diesem Abschnitt.</span>
              </div>

              <v-row v-for="(hours, rowIndex) in openingSection.hours" :key="`hours-row-${rowIndex}`" dense class="hours-row">
                <v-col cols="12" lg="4" class="text-field-col">
                  <v-text-field v-model="hours.text" label="Text" outlined dense dark hide-details="auto"></v-text-field>
                  <v-btn icon class="icon-action danger" aria-label="Zeitangabe löschen" @click="removeHour(rowIndex, openingSection.hours)"
                    ><v-icon>mdi-close</v-icon></v-btn
                  >
                </v-col>

                <v-col cols="6" sm="3" lg="2">
                  <v-menu
                    v-model="hours.fromPicker"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="hours.from"
                        label="Von"
                        prepend-inner-icon="mdi-clock-outline"
                        readonly
                        outlined
                        dense
                        dark
                        hide-details="auto"
                        v-bind="attrs"
                        v-on="on"></v-text-field>
                    </template>
                    <v-time-picker v-if="hours.fromPicker" v-model="hours.from" full-width format="24hr"></v-time-picker>
                  </v-menu>
                </v-col>

                <v-col cols="6" sm="3" lg="2">
                  <v-menu
                    v-model="hours.toPicker"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="hours.to"
                        label="Bis"
                        prepend-inner-icon="mdi-clock-outline"
                        readonly
                        outlined
                        dense
                        dark
                        hide-details="auto"
                        v-bind="attrs"
                        v-on="on"></v-text-field>
                    </template>
                    <v-time-picker v-if="hours.toPicker" v-model="hours.to" full-width format="24hr"></v-time-picker>
                  </v-menu>
                </v-col>

                <v-col cols="6" sm="3" lg="2">
                  <v-menu
                    v-model="hours.fromPicker2"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="hours.from2"
                        label="Von (2)"
                        prepend-inner-icon="mdi-clock-outline"
                        readonly
                        outlined
                        dense
                        dark
                        hide-details="auto"
                        v-bind="attrs"
                        v-on="on"></v-text-field>
                    </template>
                    <v-time-picker v-if="hours.fromPicker2" v-model="hours.from2" full-width format="24hr"></v-time-picker>
                  </v-menu>
                </v-col>

                <v-col cols="6" sm="3" lg="2">
                  <v-menu
                    v-model="hours.toPicker2"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="hours.to2"
                        label="Bis (2)"
                        prepend-inner-icon="mdi-clock-outline"
                        readonly
                        outlined
                        dense
                        dark
                        hide-details="auto"
                        v-bind="attrs"
                        v-on="on"></v-text-field>
                    </template>
                    <v-time-picker v-if="hours.toPicker2" v-model="hours.to2" full-width format="24hr"></v-time-picker>
                  </v-menu>
                </v-col>
              </v-row>

              <v-btn v-if="openingSection.title" outlined color="#58d388" class="secondary-action" @click="addNewOpeningHours(openingSection)">
                <v-icon left>mdi-plus</v-icon> Zeitangabe hinzufügen
              </v-btn>
            </section>

            <div class="panel-actions">
              <v-btn v-if="canAddSection(studio)" outlined color="#58d388" class="secondary-action" @click="addNewSection(studio)">
                <v-icon left>mdi-plus</v-icon>Neuen Abschnitt hinzufügen
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn color="#009640" depressed class="primary-action" :loading="isStudioSaving(studio)" @click="saveStudio(studio)">
                <v-icon left>mdi-content-save-outline</v-icon>Speichern
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </section>
  </studio-tool-shell>
</template>

<script>
import api from "../../../api"
import StudioToolShell from "./components/StudioToolShell.vue"

export default {
  name: "StudioDashboardOpeningHours",
  components: { StudioToolShell },
  props: {
    studios: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      panel: [],
      openingHoursStudios: [],
      savingStudios: [],
    }
  },
  async created() {
    await this.fetchOpeningHours()
    this.setStudios()
  },
  methods: {
    removeHour(index, hoursArray) {
      if (confirm("Willst du diese Uhrzeit wirklich löschen?")) hoursArray.splice(index, 1)
    },
    isStudioSaving(studio) {
      return this.savingStudios.includes(studio)
    },
    canAddSection(studio) {
      return (
        !studio.openingHours.length ||
        (studio.openingHours[studio.openingHours.length - 1].title && studio.openingHours[studio.openingHours.length - 1].hours.length)
      )
    },
    async saveStudio(studio) {
      this.savingStudios.push(studio)
      try {
        await api.saveOpeningHours(studio)
        this.$toast.success("Öffnungszeiten erfolgreich bearbeitet")
      } catch (error) {
        console.error("Fehler beim Speichern der Öffnungszeiten:", error)
        this.$toast.error("Fehler beim Speichern")
      } finally {
        this.savingStudios = this.savingStudios.filter((item) => item !== studio)
      }
    },
    removeOpeningHourTable(index, openingHours) {
      if (confirm("Willst du den Abschnitt wirklich löschen?")) openingHours.splice(index, 1)
    },
    addNewOpeningHours(openingSection) {
      openingSection.hours.push({
        text: "",
        from: null,
        fromPicker: false,
        to: null,
        toPicker: false,
        from2: null,
        fromPicker2: false,
        to2: null,
        toPicker2: false,
      })
    },
    addNewSection(studio) {
      studio.openingHours.push({ title: "", hours: [] })
    },
    setStudios() {
      this.openingHoursStudios = JSON.parse(
        JSON.stringify(
          this.studios.map((studio) => ({
            _id: studio._id,
            name: studio.name,
            openingHours: studio.openingHours || [],
            alwaysCheckin: studio.alwaysCheckin || false,
          })),
        ),
      )
    },
    async fetchOpeningHours() {
      try {
        await api.fetchOpeningHours()
      } catch (error) {
        console.error("Fehler beim Laden der Öffnungszeiten:", error)
      } finally {
        this.$emit("finishedloading")
      }
    },
  },
  watch: {
    studios() {
      this.setStudios()
    },
  },
}
</script>

<style scoped>
.hours-surface {
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem;
  background: var(--surface);
}

.surface-header,
.studio-summary,
.checkin-card,
.section-header,
.panel-actions,
.text-field-col,
.empty-row {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.125rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
}

.surface-header p,
.checkin-card span {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.count-pill,
.status-pill {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 750;
}

.count-pill {
  padding: 0.4375rem 0.75rem;
  color: #d9f8e5;
  background: rgba(0, 150, 64, 0.14);
}

.hours-panels {
  gap: 0.75rem;
}

.hours-panel {
  overflow: hidden;
  border: 1px solid var(--border) !important;
  border-radius: 1.125rem !important;
  background: rgba(20, 20, 21, 0.76) !important;
}

.hours-panel::before {
  box-shadow: none !important;
}

.hours-panel-header {
  min-height: 5rem;
  padding: 0.875rem 1rem;
}

.studio-summary {
  min-width: 0;
  width: 100%;
  gap: 0.75rem;
}

.studio-mark {
  display: grid;
  flex: 0 0 2.75rem;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.studio-summary > div {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.studio-summary strong {
  overflow: hidden;
  color: var(--text);
  font-size: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.studio-summary div span {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.status-pill {
  margin-right: 0.625rem;
  padding: 0.375rem 0.625rem;
  color: #d3d3d5;
  background: rgba(255, 255, 255, 0.07);
}

.status-pill.active {
  color: #bff3d1;
  background: rgba(0, 150, 64, 0.13);
}

.hours-panel-content ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 1rem 1rem;
}

.checkin-card {
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.checkin-card > div {
  display: flex;
  flex-direction: column;
}

.checkin-card strong {
  color: var(--text);
  font-size: 1rem;
}

.schedule-section {
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.section-header {
  gap: 0.625rem;
  margin-bottom: 0.75rem;
}

.section-header .v-input {
  flex: 1 1 auto;
}

.hours-row {
  margin-bottom: 0.25rem;
  padding: 0.375rem;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.12);
}

.text-field-col {
  gap: 0.375rem;
}

.text-field-col .v-input {
  flex: 1 1 auto;
}

.icon-action {
  flex: 0 0 2.75rem;
  width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.045) !important;
}

.icon-action.danger {
  color: #ff8a87 !important;
}

.empty-row {
  justify-content: center;
  gap: 0.5rem;
  min-height: 5rem;
  margin-bottom: 0.75rem;
  border: 1px dashed var(--border);
  border-radius: 0.875rem;
  color: var(--muted);
  font-size: 0.875rem;
}

.panel-actions {
  flex-wrap: wrap;
  gap: 0.625rem;
  padding-top: 1rem;
}

.primary-action,
.secondary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.secondary-action {
  color: #a6edc0 !important;
}

::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.13);
}

@media (hover: hover) and (pointer: fine) {
  .hours-panel:hover,
  .schedule-section:hover {
    border-color: var(--border-strong) !important;
  }
}

@media (max-width: 37.4375rem) {
  .hours-surface {
    padding: 0.875rem;
    border-radius: 1.125rem;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .hours-panel-header {
    padding: 0.75rem;
  }

  .status-pill {
    display: none;
  }

  .hours-panel-content ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 0.75rem 0.75rem;
  }

  .checkin-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .schedule-section {
    padding: 0.75rem;
  }

  .panel-actions .v-btn {
    width: 100%;
  }
}
</style>
